import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { SEO, createBreadcrumbSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { GLOSSARY, GLOSSARY_CATEGORIES } from '@/content/glossary';
import { ArrowRight, BookOpen } from 'lucide-react';

/**
 * /glossary — every term a student meets in a lesson, on a teacher profile or
 * in a blog article, defined in both languages on one page.
 *
 * Terms live in src/content/glossary.ts. The page emits DefinedTermSet plus
 * BreadcrumbList structured data and links each term to the article or landing
 * page that treats it in depth, so it doubles as an internal link hub.
 */

const SITE = 'https://hudanlilmuttaqeen.com';

const Glossary = () => {
  const { t, isRTL, language } = useLanguage();
  const dir = isRTL ? 'rtl' : 'ltr';
  const url = `${SITE}/glossary`;

  const copy = isRTL
    ? {
        title: 'مصطلحات القرآن والتجويد: معجم مختصر',
        subtitle:
          'كل مصطلح يلقاه الطالب في درسه أو في ملف معلمه أو في مقالاتنا، مشروحًا بالعربية والإنجليزية في صفحة واحدة.',
        seoTitle: 'معجم مصطلحات القرآن والتجويد | هُدًى للمتقين',
        seoDescription:
          'معجم بلغتين لمصطلحات القرآن والتجويد والحفظ والرواية: المخرج والصفة والغنة والإدغام والمد والوقف والإجازة والسند والقراءات وغيرها.',
        keywords:
          'مصطلحات التجويد, معجم التجويد, معاني مصطلحات القرآن, الإدغام والإخفاء, السند والإجازة, tajweed glossary',
        breadcrumb: 'المصطلحات',
        countLabel: 'مصطلحًا',
        jump: 'انتقل إلى',
        readMore: 'اقرأ المزيد',
        ctaTitle: 'المصطلح يُفهم حين تسمعه في التلاوة',
        ctaSubtitle: 'احجز جلسة مع معلم يطبّق هذه الأحكام على قراءتك أنت.',
        ctaPrimary: 'ابدأ بجلسة تجريبية',
        ctaSecondary: 'تصفّح المعلمين',
      }
    : {
        title: 'Quran and Tajweed Glossary',
        subtitle:
          'Every term you will meet in a lesson, on a teacher profile or in our articles, defined in English and Arabic on one page.',
        seoTitle: 'Quran and Tajweed Glossary: Terms Explained | Hudan',
        seoDescription:
          'A bilingual glossary of Quran, Tajweed, hifz and transmission terms: makhraj, sifah, ghunnah, idgham, madd, waqf, Ijazah, sanad, qiraat and more.',
        keywords:
          'tajweed glossary, quran terms explained, what is idgham, what is madd, sanad ijazah meaning, مصطلحات التجويد',
        breadcrumb: 'Glossary',
        countLabel: 'terms',
        jump: 'Jump to',
        readMore: 'Read more',
        ctaTitle: 'A term makes sense when you hear it in your own recitation',
        ctaSubtitle: 'Book a session with a teacher who applies these rules to the way you read.',
        ctaPrimary: 'Start with a trial session',
        ctaSecondary: 'Browse teachers',
      };

  const structuredData = useMemo(() => {
    const inLanguage = isRTL ? 'ar' : 'en';
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'DefinedTermSet',
          '@id': `${url}#termset`,
          name: copy.title,
          inLanguage,
          url,
          hasDefinedTerm: GLOSSARY.map((term) => ({
            '@type': 'DefinedTerm',
            name: isRTL ? term.ar : term.en,
            alternateName: isRTL ? term.en : term.ar,
            description: isRTL ? term.defAr : term.defEn,
            inDefinedTermSet: `${url}#termset`,
            url: `${url}#${term.id}`,
            termCode: term.id,
          })),
        },
        createBreadcrumbSchema(
          [
            { name: t('nav.home'), url: `${SITE}/` },
            { name: copy.breadcrumb, url },
          ],
          inLanguage
        ),
      ],
    };
    // language flips both copy and isRTL.
  }, [copy, isRTL, t, url, language]);

  return (
    <Layout>
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={copy.keywords}
        url={url}
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark" dir={dir}>
        <div className="absolute inset-0 islamic-pattern-overlay opacity-60" />
        <div className="container relative z-10 py-14 md:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <BookOpen className="h-3.5 w-3.5 text-amber-400" aria-hidden="true" />
              <span className="text-emerald-100 text-sm font-medium">
                {GLOSSARY.length} {copy.countLabel}
              </span>
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-[1.15]">{copy.title}</h1>
            <p className="text-lg text-emerald-100/80 leading-relaxed">{copy.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Category jump links */}
      <nav aria-label={copy.jump} className="border-b border-border bg-card" dir={dir}>
        <div className="container py-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{copy.jump}</span>
          {GLOSSARY_CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              className="text-sm text-foreground hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
            >
              {isRTL ? c.ar : c.en}
            </a>
          ))}
        </div>
      </nav>

      {/* Terms */}
      <section className="bg-background" dir={dir}>
        <div className="container py-12 md:py-16 max-w-4xl">
          {GLOSSARY_CATEGORIES.map((category) => {
            const terms = GLOSSARY.filter((term) => term.category === category.id);
            if (!terms.length) return null;
            return (
              <div key={category.id} id={category.id} className="mb-14 scroll-mt-24">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 pb-3 border-b border-border">
                  {isRTL ? category.ar : category.en}
                </h2>
                <dl className="space-y-7">
                  {terms.map((term) => (
                    <div key={term.id} id={term.id} className="scroll-mt-24">
                      <dt className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1.5">
                        <span className="text-lg font-semibold text-foreground">
                          {isRTL ? term.ar : term.en}
                        </span>
                        <span
                          className={`text-sm text-muted-foreground ${isRTL ? '' : 'font-arabic text-base'}`}
                          lang={isRTL ? 'en' : 'ar'}
                          dir={isRTL ? 'ltr' : 'rtl'}
                        >
                          {isRTL ? term.en : term.ar}
                        </span>
                      </dt>
                      <dd className="text-muted-foreground leading-relaxed">
                        {isRTL ? term.defAr : term.defEn}
                        {term.link && (
                          <>
                            {' '}
                            <Link
                              to={term.link}
                              className="text-amber-700 dark:text-amber-400 hover:underline whitespace-nowrap"
                            >
                              {copy.readMore}
                            </Link>
                          </>
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-card border-t border-border" dir={dir}>
        <div className="container py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{copy.ctaTitle}</h2>
          <p className="text-muted-foreground mb-7 max-w-2xl mx-auto">{copy.ctaSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-7 h-12">
              <Link to="/register/student" className="flex items-center gap-2">
                {copy.ctaPrimary}
                <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl px-7 h-12">
              <Link to="/teachers">{copy.ctaSecondary}</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Glossary;
